import React, { useState } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import { CustomPagination, TableCheckBox } from '@/components';
import { ITable, saveCurrentPageToLocalStorage, saveRowsPerPageToLocalStorage } from '@/core';
import palette from '@/theme/palette.ts';

const EmptyToolBar = () => <div />;
const EmptyPagination = () => <div />;

export type TableHeaderProps = {
  count: number;
  selectedRows: number[];
  onDelete: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  onCancel: () => void;
  onDuplicate: () => void;
  isDuplicateDisabled?: boolean;
};

export const Table = ({
  rows,
  columns,
  tableId,
  isDraggable,
  isCheckBox,
  isRowsEditable,
  selectedRows,
  onSelectRow,
  onSelectAll,
  loading,
  onEditRow,
  editRowButtonLabel,
  pagination,
  paginationOptions,
  ToolBar,
  sx,
  getRowId,
  sortModel,
  onSortModelChange,
  sortingMode,
  onDragEnd,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: ITable<any>) => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const [innerRows, setRows] = useState(rows);

  const getIsCheckBoxSelected = (rowId: number) => selectedRows?.includes(rowId) || false;

  const draggableColumn: GridColDef[] = [
    {
      field: 'drag',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          draggable
          onDragStart={(event) => handleDragStart(event, params.row.id)}
          onDragOver={(event) => handleDragOver(event, params.row.id)}
          onDrop={(event) => handleDrop(event)}
          sx={{ cursor: 'grab', width: '40px', marginRight: '30px' }}
        >
          <DragIndicatorIcon sx={{ color: 'gray' }} />
        </IconButton>
      ),
    },
  ];

  const checkBoxColumn: GridColDef[] = [
    {
      field: 'checkbox',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      renderHeader: () => (
        <TableCheckBox
          checked={rows && rows.length === selectedRows?.length && rows.length !== 0}
          onChange={() => onSelectAll && onSelectAll()}
        />
      ),
      renderCell: (params) => (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', height: '100%' }}
        >
          <TableCheckBox
            checked={getIsCheckBoxSelected(params.row.id)}
            onChange={() => onSelectRow && onSelectRow(params.row.id)}
          />
        </Stack>
      ),
    },
  ];

  const emptyColumnStart: GridColDef[] = [
    {
      field: 'emptyStart',
      headerName: '',
      width: 1,
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      renderHeader: () => <Box />,
      renderCell: () => <Box />,
    },
  ];

  const emptyColumnEnd: GridColDef[] = [
    {
      field: 'emptyEnd',
      headerName: '',
      width: 1,
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      renderHeader: () => <Box />,
      renderCell: () => <Box />,
    },
  ];

  const editRowColumn: GridColDef[] = [
    {
      field: 'edit',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      align: 'right',
      renderCell: (params) => (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', height: '100%', cursor: 'pointer' }}
          onClick={() => onEditRow && onEditRow(params.row.id)}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: '500 !important', fontSize: '0.875rem', color: palette.primary.main }}
          >
            {editRowButtonLabel ? editRowButtonLabel : t('GLOBAL.EDIT')}
          </Typography>
        </Stack>
      ),
    },
  ];

  const innerColumns = [
    ...emptyColumnStart,
    ...(isDraggable ? draggableColumn : []),
    ...(isCheckBox ? checkBoxColumn : []),
    ...columns,
    ...(isRowsEditable ? editRowColumn : []),
    ...emptyColumnEnd,
  ];
  const [draggedRowId, setDraggedRowId] = useState<number | null>(null);

  const handleDragStart = (event: React.DragEvent, rowId: number) => {
    setDraggedRowId(rowId);
  };

  const handleDragOver = (event: React.DragEvent, targetRowId: number) => {
    event.preventDefault();
    if (!draggedRowId || draggedRowId === targetRowId) return;

    const updatedRows = [...innerRows];
    const draggedRow = updatedRows.find((row) => row.id === draggedRowId);
    const targetRowIndex = updatedRows.findIndex((row) => row.id === targetRowId);
    const draggedRowIndex = updatedRows.findIndex((row) => row.id === draggedRowId);

    updatedRows.splice(draggedRowIndex, 1);
    updatedRows.splice(targetRowIndex, 0, draggedRow!);

    setRows(updatedRows);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    if (draggedRowId !== null) {
      onDragEnd?.(innerRows, draggedRowId);
    }
    setDraggedRowId(null);
  };

  return (
    <div style={{ width: '100%' }}>
      <DataGrid
        apiRef={apiRef}
        rows={innerRows}
        columns={innerColumns}
        disableColumnFilter
        disableColumnSelector
        ignoreDiacritics
        disableColumnMenu
        hideFooterSelectedRowCount
        disableColumnResize
        disableRowSelectionOnClick
        loading={loading}
        disableDensitySelector
        slots={{
          toolbar: ToolBar || EmptyToolBar,
          pagination: () =>
            pagination && paginationOptions ? (
              <CustomPagination
                page={rows && rows.length > 0 ? paginationOptions.currentPage : 0}
                rowsPerPageOptions={paginationOptions.rowsPerPageOptions}
                onPageChange={(newPage) => {
                  paginationOptions.onPageChange(newPage);
                  saveCurrentPageToLocalStorage(tableId, newPage);
                }}
                onPageSizeChange={(newPageSize) => {
                  paginationOptions.onPageSizeChange(newPageSize);
                  saveRowsPerPageToLocalStorage(tableId, newPageSize);
                }}
                pageSize={paginationOptions.pageSize}
                rowCount={paginationOptions.totalRows}
              />
            ) : (
              <EmptyPagination />
            ),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        sx={{
          border: 'none',
          outline: 'none',
          color: palette.text.primary,
          '& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeader': {
            backgroundColor: palette.surface.warm,
          },
          '.MuiDataGrid-cell .MuiTypography-root': {
            fontWeight: '400',
          },
          '& .MuiDataGrid-main': {
            minHeight: '830px',
          },
          ...sx,
        }}
        getRowId={getRowId}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingMode={sortingMode ?? 'client'}
      />
    </div>
  );
};
